/**
 * useCloudSync - Hook for syncing local data with Firebase
 * Provides offline-first experience with automatic cloud sync
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../services/firebase';
import firestoreSync from '../services/firestoreSync';
import firebaseStorage from '../services/firebaseStorage';
import taskDatabase from '../services/taskDatabase';

/**
 * Main hook for cloud synchronization
 */
export function useCloudSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const syncQueueRef = useRef([]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });

    return () => unsubscribe();
  }, []);

  // Process sync queue when online
  useEffect(() => {
    if (isOnline && isAuthenticated && syncQueueRef.current.length > 0) {
      processSyncQueue();
    }
  }, [isOnline, isAuthenticated]);

  /**
   * Process pending sync operations
   */
  const processSyncQueue = useCallback(async () => {
    if (syncQueueRef.current.length === 0) return;

    setIsSyncing(true);
    try {
      const queue = [...syncQueueRef.current];
      syncQueueRef.current = [];

      for (const operation of queue) {
        try {
          await operation();
        } catch (err) {
          console.error('Sync operation failed:', err);
          // Re-queue failed operations
          syncQueueRef.current.push(operation);
        }
      }

      setLastSyncTime(new Date());
    } catch (error) {
      setSyncError(error.message);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  /**
   * Add operation to sync queue
   */
  const queueSync = useCallback((operation) => {
    syncQueueRef.current.push(operation);
    if (isOnline && isAuthenticated) {
      processSyncQueue();
    }
  }, [isOnline, isAuthenticated, processSyncQueue]);

  /**
   * Sync a task to cloud
   */
  const syncTask = useCallback(async (task) => {
    // Always save locally first
    await taskDatabase.saveTask(task);

    // Queue cloud sync
    if (isAuthenticated) {
      queueSync(() => firestoreSync.saveTask(task));
    }
  }, [isAuthenticated, queueSync]);

  /**
   * Sync multiple tasks to cloud
   */
  const syncTasks = useCallback(async (tasks) => {
    // Save locally first
    for (const task of tasks) {
      await taskDatabase.saveTask(task);
    }

    // Queue cloud sync
    if (isAuthenticated) {
      queueSync(() => firestoreSync.saveTasks(tasks));
    }
  }, [isAuthenticated, queueSync]);

  /**
   * Delete a task and sync
   */
  const deleteTask = useCallback(async (taskId) => {
    // Delete locally first
    await taskDatabase.deleteTask(taskId);

    // Queue cloud sync
    if (isAuthenticated) {
      queueSync(() => firestoreSync.deleteTask(taskId));
    }
  }, [isAuthenticated, queueSync]);

  /**
   * Complete a task and sync
   */
  const completeTask = useCallback(async (task) => {
    // Update locally first
    const completedTask = {
      ...task,
      completed: true,
      completedAt: new Date().toISOString()
    };

    await taskDatabase.deleteTask(task.id);
    await taskDatabase.saveCompletedTask(completedTask);

    // Queue cloud sync
    if (isAuthenticated) {
      queueSync(() => firestoreSync.completeTask(completedTask));
    }

    return completedTask;
  }, [isAuthenticated, queueSync]);

  /**
   * Full sync - pull from cloud and merge with local
   */
  const fullSync = useCallback(async () => {
    if (!isAuthenticated || !isOnline) {
      throw new Error('Cannot sync: offline or not authenticated');
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      // Fetch cloud data
      const cloudData = await firestoreSync.fetchFromCloud();

      // Get local data
      const localData = await taskDatabase.exportAllExtended();

      // Merge strategy: cloud wins for conflicts (by updatedAt)
      const mergedTasks = mergeTasks(localData.tasks || [], cloudData.tasks || []);
      const mergedCompleted = mergeTasks(localData.completed || [], cloudData.completed || []);
      const mergedTemplates = mergeTasks(localData.customTemplates || [], cloudData.templates || []);

      // Save merged data locally
      await taskDatabase.importAll({
        tasks: mergedTasks,
        completed: mergedCompleted,
        customTemplates: mergedTemplates,
        settings: cloudData.settings || localData.settings,
        gamification: cloudData.gamification || localData.gamification
      });

      // Push local-only items to cloud
      const localOnlyTasks = mergedTasks.filter(t => !t.syncedAt);
      if (localOnlyTasks.length > 0) {
        await firestoreSync.saveTasks(localOnlyTasks);
      }

      setLastSyncTime(new Date());
      localStorage.setItem('adhd_last_sync', new Date().toISOString());

      return {
        tasks: mergedTasks,
        completed: mergedCompleted,
        templates: mergedTemplates
      };
    } catch (error) {
      setSyncError(error.message);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, isOnline]);

  /**
   * Push all local data to cloud
   */
  const pushToCloud = useCallback(async () => {
    if (!isAuthenticated || !isOnline) {
      throw new Error('Cannot push: offline or not authenticated');
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const localData = await taskDatabase.exportAllExtended();

      await firestoreSync.syncToCloud({
        tasks: localData.tasks,
        completed: localData.completed,
        templates: localData.customTemplates,
        settings: {
          schedule: localData.userSchedule,
          autoGeneration: localData.autoGeneration
        },
        gamification: localData.gamification
      });

      setLastSyncTime(new Date());
      localStorage.setItem('adhd_last_sync', new Date().toISOString());

      return true;
    } catch (error) {
      setSyncError(error.message);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, isOnline]);

  /**
   * Pull all data from cloud
   */
  const pullFromCloud = useCallback(async () => {
    if (!isAuthenticated || !isOnline) {
      throw new Error('Cannot pull: offline or not authenticated');
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const cloudData = await firestoreSync.fetchFromCloud();

      // Replace local data with cloud data
      await taskDatabase.importAll({
        tasks: cloudData.tasks,
        completed: cloudData.completed,
        customTemplates: cloudData.templates,
        proofs: cloudData.proofs,
        settings: cloudData.settings,
        gamification: cloudData.gamification,
        moodTracking: cloudData.moodHistory
      });

      setLastSyncTime(new Date());
      localStorage.setItem('adhd_last_sync', new Date().toISOString());

      return cloudData;
    } catch (error) {
      setSyncError(error.message);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, isOnline]);

  return {
    // State
    isSyncing,
    syncError,
    lastSyncTime,
    isOnline,
    isAuthenticated,

    // Task operations
    syncTask,
    syncTasks,
    deleteTask,
    completeTask,

    // Full sync operations
    fullSync,
    pushToCloud,
    pullFromCloud,

    // Clear error
    clearError: () => setSyncError(null)
  };
}

/**
 * Hook for uploading proofs with cloud sync
 */
export function useProofUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const uploadProof = useCallback(async (file, taskId, options = {}) => {
    const isAuth = firestoreSync.isAuthenticated();

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      let proofData;

      if (isAuth) {
        // Upload to Firebase Storage
        const result = await firebaseStorage.uploadCompressedProof(
          file,
          taskId,
          (progress) => setUploadProgress(progress)
        );

        proofData = {
          id: `proof_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          taskId,
          type: 'image',
          url: result.url,
          storagePath: result.path,
          fileName: result.fileName,
          createdAt: new Date().toISOString(),
          ...options
        };

        // Save to Firestore
        await firestoreSync.saveProof(proofData);
      } else {
        // Save locally as base64
        const base64 = await firebaseStorage.blobToBase64(file);

        proofData = {
          id: `proof_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          taskId,
          type: 'image',
          data: base64,
          createdAt: new Date().toISOString(),
          isLocal: true,
          ...options
        };
      }

      // Always save to local DB
      await taskDatabase.saveProof(proofData);

      setUploadProgress(100);
      return proofData;
    } catch (error) {
      setUploadError(error.message);
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const deleteProof = useCallback(async (proof) => {
    try {
      // Delete from Firebase Storage if applicable
      if (proof.storagePath && firestoreSync.isAuthenticated()) {
        await firebaseStorage.deleteFile(proof.storagePath);
        await firestoreSync.deleteProof(proof.id);
      }

      // Delete from local DB
      await taskDatabase.deleteProof(proof.id);

      return true;
    } catch (error) {
      setUploadError(error.message);
      throw error;
    }
  }, []);

  return {
    uploadProof,
    deleteProof,
    isUploading,
    uploadProgress,
    uploadError,
    clearError: () => setUploadError(null)
  };
}

/**
 * Helper function to merge task arrays
 * @param {Array} local - Local tasks
 * @param {Array} cloud - Cloud tasks
 * @returns {Array} Merged tasks
 */
function mergeTasks(local, cloud) {
  const merged = new Map();

  // Add cloud items first
  cloud.forEach(item => {
    merged.set(item.id, item);
  });

  // Merge local items
  local.forEach(item => {
    const existing = merged.get(item.id);
    if (!existing) {
      merged.set(item.id, item);
    } else {
      // Compare timestamps, keep newer
      const localTime = new Date(item.updatedAt || item.createdAt || 0).getTime();
      const cloudTime = new Date(existing.updatedAt || existing.createdAt || 0).getTime();

      if (localTime > cloudTime) {
        merged.set(item.id, item);
      }
    }
  });

  return Array.from(merged.values());
}

export default useCloudSync;
